import { Form, Input, Select, Button, message, Modal, Row, Col, Divider, Space } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useEffect, useState, useRef } from "react";
import { CloseOutlined } from "@ant-design/icons";
import AnimateItems from "../../../utils/AnimateItems";
import axios from "axios";
import PersonaForm from "./persona/PersonaForm";
import { useAuth } from "../../../layout/auth";

// The set of fields on the verification record that describe "whoever PA
// currently points to" — they only make sense together, so they're
// snapshotted and reverted as one unit.
const PA_FIELDS = ["PA", "DatoPA", "TelefonoFijoPA", "TelefonoMovilPA", "CorreoElectronicoPA", "CargoPA", "FolioPA", "DomicilioPA"];

const pickPAFields = (source) =>
  PA_FIELDS.reduce((acc, field) => {
    acc[field] = source?.[field] ?? null;
    return acc;
  }, {});

export default function PAInfoForm(props) {
  const { title, visible, mainData: verification, setMainData: setVerification, setVisible } = props;
  const [personaFormVisible, setPersonaFormVisible] = useState(false);
  const [datosPersona, setDatosPersona] = useState();
  const [telefonosFijos, setTelefonosFijos] = useState();
  const [telefonosMoviles, setTelefonosMoviles] = useState();
  const [correosPersona, setCorreosPersona] = useState();
  const [personasList, setPersonasList] = useState();
  const [foliosPersona, setFoliosPersona] = useState();
  const [domiciliosPersona, setDomiciliosPersona] = useState();
  const [openDropdown, setOpenDropdown] = useState(false);
  const [personaEditing, setPersonaEditing] = useState();
  const auth = useAuth();

  // Snapshot of the PA-related fields exactly as they were the moment the
  // modal opened, so "Cancelar" can genuinely revert to this — including
  // undoing an already-saved new persona created during this session.
  const originalPADataRef = useRef();

  const [form] = Form.useForm();

  useEffect(() => {
    if (visible) {
      originalPADataRef.current = pickPAFields(verification);
    }
    // Deliberately only depends on `visible`: we want this captured once,
    // at the moment the modal opens, not every time `verification` changes
    // afterward (which happens as soon as a persona auto-saves).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Extracted so it can be reused both on initial load and right after a
  // new persona is created (so the dropdown includes them immediately).
  const fetchPersonasList = () => {
    const personasPromise =
      auth.Rol === "director"
        ? axios.get(`${process.env.REACT_APP_API_URL}/personas`)
        : axios.get(`${process.env.REACT_APP_API_URL}/personas/usuario/${auth.UID}`);

    return personasPromise
      .then((p) => {
        setPersonasList(p.data.filter((persona) => persona.TipoPersonaID === 1));
      })
      .catch((error) => {
        console.log(error);
        message.error(error.message);
      });
  };

  // The single source of truth for persisting this verificación. Used by
  // the main "Guardar" button AND immediately after a new persona is
  // created, so a newly-created persona is genuinely linked in the
  // database right away — not just displayed as if it were.
  const saveVerification = (overrides) => {
    const verificationData = { ...verification, ...form.getFieldsValue(), ...overrides };
    return axios
      .put(`${process.env.REACT_APP_API_URL}/verificaciones/${verification.VerificacionID}`, verificationData)
      .then(() =>
        axios.get(`${process.env.REACT_APP_API_URL}/verificaciones/${verification.VerificacionID}`).then((res) => {
          setVerification(res.data[0]);
          return true;
        })
      )
      .catch((err) => {
        console.log(err);
        message.error("Error al actualizar la verificación");
        return false;
      });
  };

  // Extracted so it can be reused by the Select's onChange and right after
  // a new persona is created. Returns a promise so callers can chain a
  // save immediately after the related data has finished loading.
  const handlePersonaSelected = (value) => {
    if (value) {
      return Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/datos/personainvolucrada/${value}`),
        axios.get(`${process.env.REACT_APP_API_URL}/telefonos/persona/${value}`),
        axios.get(`${process.env.REACT_APP_API_URL}/correos/persona/${value}`),
        axios.get(`${process.env.REACT_APP_API_URL}/datos/folios/${value}`),
        axios.get(`${process.env.REACT_APP_API_URL}/datos/domicilios/${value}`),
      ])
        .then(([d, t, c, f, dm]) => {
          setDatosPersona(d.data);
          setTelefonosFijos(t.data.filter((telefono) => telefono.TipoTelefonoID === 2));
          setTelefonosMoviles(t.data.filter((telefono) => telefono.TipoTelefonoID === 1));
          setCorreosPersona(c.data);
          setFoliosPersona(f.data);
          setDomiciliosPersona(dm.data);
          form.setFieldsValue({
            DatoPA: d.data[0]?.DatoID,
            TelefonoFijoPA: t.data.filter((telefono) => telefono.TipoTelefonoID === 2)[0]?.TelefonoID,
            TelefonoMovilPA: t.data.filter((telefono) => telefono.TipoTelefonoID === 1)[0]?.TelefonoID,
            CorreoElectronicoPA: c.data[0]?.CorreoElectronicoID,
            FolioPA: f.data[0]?.FolioID,
            DomicilioPA: dm.data[0]?.DomicilioID,
          });
        })
        .catch((error) => {
          console.log(error);
          message.error("Error al cargar los datos");
        });
    } else {
      setDatosPersona(null);
      setTelefonosFijos(null);
      setTelefonosMoviles(null);
      setCorreosPersona(null);
      setFoliosPersona(null);
      setDomiciliosPersona(null);
      form.setFieldsValue({
        DatoPA: null,
        TelefonoFijoPA: null,
        TelefonoMovilPA: null,
        CorreoElectronicoPA: null,
        FolioPA: null,
        DomicilioPA: null,
      });
      return Promise.resolve();
    }
  };

  // Called by PersonaForm right after a brand-new persona is saved:
  // select them, load their (empty) data, and then IMMEDIATELY persist the
  // link to this verificación (the same save the "Guardar" button does) —
  // so the persona is genuinely linked in the database right away, and the
  // next fetch of personasList will legitimately include them. If the user
  // later clicks "Cancelar", this save gets reverted via originalPADataRef.
  const handlePersonaCreated = (newPersonaId) => {
    form.setFieldsValue({ PA: newPersonaId });
    handlePersonaSelected(newPersonaId).then(() => {
      saveVerification().then((ok) => {
        if (ok) {
          fetchPersonasList();
          message.success("La nueva persona quedó guardada y vinculada a esta verificación.");
        }
      });
    });
  };

  // Called by PersonaForm right after a teléfono is added to the persona
  // currently being edited: select it in the matching field and confirm.
  const handleTelefonoAdded = (newTelefonoId, tipoTelefonoId) => {
    if (tipoTelefonoId === 2) {
      form.setFieldsValue({ TelefonoFijoPA: newTelefonoId });
      message.success("El nuevo teléfono fijo ya quedó seleccionado en el formulario.");
    } else if (tipoTelefonoId === 1) {
      form.setFieldsValue({ TelefonoMovilPA: newTelefonoId });
      message.success("El nuevo teléfono móvil ya quedó seleccionado en el formulario.");
    }
  };

  // Called by PersonaForm right after a correo is added to the persona
  // currently being edited: select it and confirm.
  const handleCorreoAdded = (newCorreoId) => {
    form.setFieldsValue({ CorreoElectronicoPA: newCorreoId });
    message.success("El nuevo correo ya quedó seleccionado en el formulario.");
  };

  // Called by PersonaForm right after a dato/identificación is added or
  // edited for the persona currently being edited. "Dato", "Folio" and
  // "Domicilio" are really the same `datos` table filtered three
  // different ways by the backend, and DatoForm has no idea which of the
  // three a given TipoDatoID belongs to — so instead of guessing on the
  // frontend, we re-run all three of this screen's own queries and check
  // which one actually contains the new/edited DatoID, then select that
  // field. If it belongs to none of the three (e.g. a TipoDatoID excluded
  // by all three endpoints — see datos.js), warn instead of going silent.
  const handleDatoAdded = (newDatoId) => {
    if (!personaEditing) return;
    Promise.all([
      axios.get(`${process.env.REACT_APP_API_URL}/datos/personainvolucrada/${personaEditing}`),
      axios.get(`${process.env.REACT_APP_API_URL}/datos/folios/${personaEditing}`),
      axios.get(`${process.env.REACT_APP_API_URL}/datos/domicilios/${personaEditing}`),
    ])
      .then(([d, f, dm]) => {
        setDatosPersona(d.data);
        setFoliosPersona(f.data);
        setDomiciliosPersona(dm.data);

        if (d.data.some((item) => item.DatoID === newDatoId)) {
          form.setFieldsValue({ DatoPA: newDatoId });
          message.success("La nueva identificación ya quedó seleccionada en el formulario.");
        } else if (f.data.some((item) => item.DatoID === newDatoId)) {
          form.setFieldsValue({ FolioPA: newDatoId });
          message.success("El nuevo folio ya quedó seleccionado en el formulario.");
        } else if (dm.data.some((item) => item.DatoID === newDatoId)) {
          form.setFieldsValue({ DomicilioPA: newDatoId });
          message.success("El nuevo domicilio ya quedó seleccionado en el formulario.");
        } else {
          message.warning(
            "El tipo de identificación que agregaste no aplica como Dato, Folio ni Domicilio para esta persona."
          );
        }
      })
      .catch((error) => {
        console.log(error);
        message.error("Error al actualizar identificaciones/folios/domicilios");
      });
  };

  // "Cancelar" reverts the PA-related fields back to exactly what they
  // were when the modal opened — genuinely writing that back to the
  // database, since a persona created during this session may have
  // already been auto-saved and linked.
  const handleCancel = () => {
    saveVerification(originalPADataRef.current).then((ok) => {
      if (ok) setVisible(false);
    });
  };

  useEffect(() => {
    visible &&
      Promise.all([
        fetchPersonasList(),
        axios.get(`${process.env.REACT_APP_API_URL}/datos/personainvolucrada/${verification?.PA}`),
        axios.get(`${process.env.REACT_APP_API_URL}/telefonos/persona/${verification?.PA}`),
        axios.get(`${process.env.REACT_APP_API_URL}/correos/persona/${verification?.PA}`),
        axios.get(`${process.env.REACT_APP_API_URL}/datos/folios/${verification?.PA}`),
        axios.get(`${process.env.REACT_APP_API_URL}/datos/domicilios/${verification?.PA}`),
      ])
        .then(([, d, t, c, f, dm]) => {
          setDatosPersona(d.data);
          setTelefonosFijos(t.data.filter((telefono) => telefono.TipoTelefonoID === 2));
          setTelefonosMoviles(t.data.filter((telefono) => telefono.TipoTelefonoID === 1));
          setCorreosPersona(c.data);
          setFoliosPersona(f.data);
          setDomiciliosPersona(dm.data);
          form.setFieldsValue(verification);
        })
        .catch((error) => {
          console.log(error);
          message.error(error.message);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verification, form, visible]);

  const clearSelectProps = {
    style: { width: "100%" },
    allowClear: true,
    clearIcon: <CloseOutlined style={{ color: "red" }} />,
    showSearch: true,
    filterOption: (input, option) => option.children?.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0,
  };

  const colResposivity = {
    xs: 24,
    sm: 24,
    md: 12,
    lg: 12,
    xl: 12,
    xxl: 12,
  };

  return (
    <>
      <Modal title={title} visible={visible} onCancel={() => setVisible(false)} footer={null} width={800} centered>
        <Form form={form} layout="vertical">
          <AnimateItems delay={0.15}>
            <Row gutter={[40, 0]}>
              <Col {...colResposivity}>
                <Form.Item name="PA" label="Persona que atiende la visita">
                  <Select
                    onChange={handlePersonaSelected}
                    {...clearSelectProps}
                    open={openDropdown}
                    onDropdownVisibleChange={(visibility) => setOpenDropdown(visibility)}
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <div style={{ float: "right" }}>
                          <Divider style={{ margin: "4px 0" }} />
                          <Space direction="horizontal">
                            <Button
                              icon={<EditOutlined />}
                              onClick={() => {
                                setPersonaEditing(form.getFieldValue("PA"));
                                setOpenDropdown(false);
                                setTimeout(() => {
                                  setPersonaFormVisible(true);
                                }, 250);
                              }}
                            >
                              Editar persona
                            </Button>
                            <Button
                              icon={<PlusOutlined />}
                              onClick={() => {
                                setPersonaEditing(null);
                                setOpenDropdown(false);
                                setTimeout(() => {
                                  setPersonaFormVisible(true);
                                }, 250);
                              }}
                            >
                              Agregar persona
                            </Button>
                          </Space>
                        </div>
                      </>
                    )}
                  >
                    {personasList?.map((persona) => (
                      <Select.Option key={persona.PersonaID} value={persona.PersonaID}>
                        {persona.NombreCompleto}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="DatoPA" label="Dato de la persona que atiende la visita">
                  <Select {...clearSelectProps}>
                    {datosPersona?.map((dato) => (
                      <Select.Option key={dato.DatoID} value={dato.DatoID}>
                        {dato.TipoDato_Corto + ": " + dato.Dato}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Row gutter={[16, 0]}>
                  <Col span={12}>
                    <Form.Item name="TelefonoFijoPA" label="Teléfono fijo">
                      <Select {...clearSelectProps}>
                        {telefonosFijos?.map((telefono) => (
                          <Select.Option key={telefono.TelefonoID} value={telefono.TelefonoID}>
                            {telefono.Telefono}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="TelefonoMovilPA" label="Teléfono móvil">
                      <Select {...clearSelectProps}>
                        {telefonosMoviles?.map((telefono) => (
                          <Select.Option key={telefono.TelefonoID} value={telefono.TelefonoID}>
                            {telefono.Telefono}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="CorreoElectronicoPA" label="Correo electrónico">
                  <Select {...clearSelectProps}>
                    {correosPersona?.map((correo) => (
                      <Select.Option key={correo.CorreoElectronicoID} value={correo.CorreoElectronicoID}>
                        {correo.CorreoElectronico}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col {...colResposivity}>
                <Form.Item name="CargoPA" label="Cargo">
                  <Input />
                </Form.Item>
                <Form.Item name="FolioPA" label="Folio">
                  <Select {...clearSelectProps}>
                    {foliosPersona?.map((folio) => (
                      <Select.Option key={folio.DatoID} value={folio.DatoID}>
                        {folio.TipoDato_Corto + ": " + folio.Dato}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="DomicilioPA" label="Domicilio">
                  <Select {...clearSelectProps}>
                    {domiciliosPersona?.map((domicilio) => (
                      <Select.Option key={domicilio.DatoID} value={domicilio.DatoID}>
                        {domicilio.TipoDato_Corto + ": " + domicilio.Dato}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </AnimateItems>
        </Form>
        <PersonaForm
          setPersonasList={setPersonasList}
          setDatosPersona={setDatosPersona}
          setTelefonosFijos={setTelefonosFijos}
          setTelefonosMoviles={setTelefonosMoviles}
          setCorreosPersona={setCorreosPersona}
          setFoliosPersona={setFoliosPersona}
          setDomiciliosPersona={setDomiciliosPersona}
          personaEditing={personaEditing}
          setPersonaEditing={setPersonaEditing}
          personaFormVisible={personaFormVisible}
          setPersonaFormVisible={setPersonaFormVisible}
          refreshPersonasList={fetchPersonasList}
          onPersonaCreated={handlePersonaCreated}
          onTelefonoAdded={handleTelefonoAdded}
          onCorreoAdded={handleCorreoAdded}
          onDatoChanged={handleDatoAdded}
          onCancel={() => setPersonaFormVisible(false)}
        />
        <Button
          type="primary"
          onClick={() => {
            saveVerification().then((ok) => {
              if (ok) setVisible(false);
            });
          }}
        >
          Guardar
        </Button>
        <Button onClick={handleCancel}>Cancelar</Button>
      </Modal>
    </>
  );
}